import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { telHref, waHref, mailHref, PHONE, EMAIL, LOGO_IMAGE } from "@/constants/site";
import useCallTracking from "@/hooks/useCallTracking";
import { useT } from "@/i18n/LanguageContext";

export default function Footer() {
  const year = new Date().getFullYear();
  const trackCall = useCallTracking();
  const { t } = useT();

  return (
    <footer
      data-testid={FOOTER.section}
      className="relative bg-[#0A0A0A] text-white pt-24 lg:pt-32 pb-10 overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#1E63FF]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Big CTA */}
        <div className="border-b border-white/10 pb-16 lg:pb-20">
          <div className="overline text-[#1E63FF]">{t.footer.overline}</div>
          <h2 className="font-display font-semibold text-4xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.95] mt-5 max-w-5xl">
            {t.footer.big1}<br />
            <a
              data-testid={FOOTER.callBig}
              href={telHref}
              onClick={trackCall("footer-big")}
              className="inline-block text-[#1E63FF] hover:underline underline-offset-8 decoration-2"
            >
              {t.footer.big2} {PHONE}
            </a>
            <br />
            <span className="text-white/50">{t.footer.big3}</span>
          </h2>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={telHref}
              onClick={trackCall("footer-card")}
              className="btn-blue h-14 px-7 rounded-full inline-flex items-center gap-2.5 text-sm font-semibold"
            >
              <Phone className="w-4 h-4" />
              {t.footer.callBtn}
            </a>
            <a
              href={waHref()}
              target="_blank"
              rel="noopener noreferrer"
              className="h-14 px-7 rounded-full inline-flex items-center gap-2.5 text-sm font-semibold bg-white text-[#0A0A0A] hover:bg-[#1E63FF] hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              {t.footer.whatsappBtn}
            </a>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-14">
          <div className="md:col-span-5">
            <img
              src={LOGO_IMAGE}
              alt="TyresPro Ltd"
              className="h-24 w-auto object-contain"
            />
            <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-md">
              {t.footer.desc}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-white/50">{t.footer.exploreLabel}</div>
            <ul className="mt-5 space-y-3 text-sm">
              <li><a href="#services" className="hover:text-[#1E63FF] transition-colors">{t.nav.services}</a></li>
              <li><a href="#areas" className="hover:text-[#1E63FF] transition-colors">{t.nav.areas}</a></li>
              <li><a href="#faq" className="hover:text-[#1E63FF] transition-colors">{t.nav.faq}</a></li>
              <li><a href="#contact" className="hover:text-[#1E63FF] transition-colors">{t.nav.contact}</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline text-white/50">{t.footer.contactLabel}</div>
            <ul className="mt-5 space-y-4 text-sm">
              <li>
                <a href={telHref} className="flex items-center gap-3 hover:text-[#1E63FF] transition-colors">
                  <Phone className="w-4 h-4" /> {PHONE}
                </a>
              </li>
              <li>
                <a href={waHref()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#1E63FF] transition-colors">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </li>
              <li>
                <a href={mailHref} className="flex items-center gap-3 hover:text-[#1E63FF] transition-colors break-all">
                  <Mail className="w-4 h-4 shrink-0" /> {EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <MapPin className="w-4 h-4" /> {t.contact.serviceAreaValue}
              </li>
            </ul>
          </div>
        </div>

        {/* Policy notice */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1E63FF] shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-white"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </span>
            <div className="text-xs sm:text-sm text-white/80 leading-relaxed">
              <span className="font-semibold text-[#7FB0FF] mr-1.5">{t.policy.label}:</span>
              {t.policy.text}
            </div>
          </div>
        </div>

        {/* Base */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/50">
          <p>© {year} TyresPro Ltd. {t.footer.rights}</p>
          <div className="flex items-center gap-5">
            <a href="/privacy.html" className="hover:text-white transition-colors">{t.footer.privacy}</a>
            <a href="/terms.html" className="hover:text-white transition-colors">{t.footer.terms}</a>
            <span>{t.footer.base}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
